import { createContext, useContext, useEffect, useState, ReactNode, useMemo, useRef } from 'react'
import { useApi, useAuth } from '../../Hooks/'
import { useSubscriptions } from './SubscriptionContext'
import { DeployStatus, Tenant } from '../../Interfaces/Types'
import { useNavigate } from 'react-router-dom'
import { sameInstant } from '../../Utilities/helpers/helpers'

// How long an optimistic status may shield a tenant from being overwritten by
// the poll while the backend catches up.
const OPTIMISTIC_STATUS_TTL_MS = 60000

const TenantContext = createContext<any>({})

interface TenantProviderProps {
  children: ReactNode
  refreshInterval?: number
}

export function TenantProvider({ children, refreshInterval = 0 }: TenantProviderProps) {
  const { isLoggedIn } = useAuth()
  const { fetchApi } = useApi()
  const { selectedSubscriptionId } = useSubscriptions()
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null)
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null)
  const [isInitialFetch, setIsInitialFetch] = useState(true)
  const history = useNavigate()

  // Optimistic status overlay: statuses set by the UI (e.g. 'updating' right
  // after clicking Update) that shield the tenant from being reverted by the
  // poll until the backend reports a changed status or the TTL expires.
  // Keyed by tenant id. A Map (not a plain object) so a server-provided id can
  // never write through to Object.prototype (no property-injection surface).
  const optimisticStatuses = useRef<Map<string, { status: DeployStatus; baseline: DeployStatus; expires: number }>>(new Map())

  // Saved client-secret expiries, kept over the poll until the backend reports the saved value:
  // a poll already in flight when the save ran would otherwise put the old date back.
  const optimisticExpiries = useRef<Map<string, { value: string | null; expires: number }>>(new Map())

  const applyOptimisticStatuses = (list: Tenant[]): Tenant[] =>
    list.map((tenant) => {
      const optimistic = optimisticStatuses.current.get(tenant.id)
      if (!optimistic) return tenant
      // Backend moved on (to the optimistic status or any other) or the
      // overlay expired: drop it and trust the backend again.
      if (tenant.deployStatus !== optimistic.baseline || Date.now() > optimistic.expires) {
        optimisticStatuses.current.delete(tenant.id)
        return tenant
      }
      return { ...tenant, deployStatus: optimistic.status }
    })

  const applyOptimisticExpiries = (list: Tenant[]): Tenant[] =>
    list.map((tenant) => {
      const optimistic = optimisticExpiries.current.get(tenant.id)
      if (!optimistic) return tenant
      if (sameInstant(tenant.clientSecretExpiresOnUtc, optimistic.value) || Date.now() > optimistic.expires) {
        optimisticExpiries.current.delete(tenant.id)
        return tenant
      }
      return { ...tenant, clientSecretExpiresOnUtc: optimistic.value }
    })

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    async function fetchTenants() {
      if (!selectedSubscriptionId || !isLoggedIn) return

      try {
        if (isInitialFetch) {
          setLoading(true)
        }

        const response = await fetchApi({
          endpoint: `/api/subscription/${selectedSubscriptionId}/tenants`,
          method: 'GET'
        })

        const merged = applyOptimisticExpiries(applyOptimisticStatuses(response.tenants || []))
        setTenants((prev) => (JSON.stringify(merged) !== JSON.stringify(prev) ? merged : prev))
      } catch (error) {
        console.error('Failed to fetch tenants', error)
      } finally {
        setLoading(false)
        setIsInitialFetch(false)
      }
    }

    if (selectedSubscriptionId) {
      fetchTenants()

      if (refreshInterval > 0) {
        interval = setInterval(() => {
          fetchTenants()
        }, refreshInterval)
      }
    }

    return () => {
      if (interval) clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchApi, selectedSubscriptionId, refreshInterval, isLoggedIn, isInitialFetch])

  useEffect(() => {
    if (selectedTenantId) {
      const tenant = tenants.find((tenant) => tenant.id === selectedTenantId) || null
      setSelectedTenant(tenant)
    } else {
      setSelectedTenant(null)
    }
  }, [selectedTenantId, tenants])

  useEffect(() => {
    console.log('Resetting tenants state due to subscription change')
    setTenants([])
    setSelectedTenantId(null)
    setSelectedTenant(null)
    setIsInitialFetch(true)
  }, [selectedSubscriptionId])

  const addTenant = (newTenant: Tenant) => {
    setTenants((prevTenants) => [...prevTenants, newTenant])
    setSelectedTenantId(newTenant.id)
  }

  // Optimistically set a tenant's deploy status in the UI (list + selected
  // tenant update instantly); the poll keeps it until the backend catches up.
  const setTenantDeployStatus = (id: string, status: DeployStatus) => {
    setTenants((prevTenants) =>
      prevTenants.map((tenant) => {
        if (tenant.id !== id) return tenant
        const existing = optimisticStatuses.current.get(id)
        optimisticStatuses.current.set(id, {
          status,
          // Keep the original backend status as baseline when stacking updates.
          baseline: existing ? existing.baseline : tenant.deployStatus,
          expires: Date.now() + OPTIMISTIC_STATUS_TTL_MS
        })
        return { ...tenant, deployStatus: status }
      })
    )
  }

  // Roll an optimistic status back to what the backend last reported (e.g.
  // when the update request itself failed).
  const revertTenantDeployStatus = (id: string) => {
    const optimistic = optimisticStatuses.current.get(id)
    if (!optimistic) return
    optimisticStatuses.current.delete(id)
    setTenants((prevTenants) => prevTenants.map((tenant) => (tenant.id === id ? { ...tenant, deployStatus: optimistic.baseline } : tenant)))
  }

  // The expiry is saved outside the deploy flow, so the list reflects it straight away
  // instead of on the next poll.
  const setTenantClientSecretExpiry = (id: string, expiresOnUtc: string | null) => {
    optimisticExpiries.current.set(id, { value: expiresOnUtc, expires: Date.now() + OPTIMISTIC_STATUS_TTL_MS })
    setTenants((prevTenants) =>
      prevTenants.map((tenant) => (tenant.id === id ? { ...tenant, clientSecretExpiresOnUtc: expiresOnUtc } : tenant))
    )
  }

  const handleSetSelectedTenantId = (id: string | null, updateHistory: boolean = true) => {
    setSelectedTenantId(id)
    if (updateHistory) {
      if (id) {
        history(`/subscriptions/${selectedSubscriptionId}/tenant/${id}`)
      } else {
        history(`/subscriptions/${selectedSubscriptionId}`)
      }
    }
  }

  const contextValue = useMemo(
    () => ({
      tenants,
      loading,
      selectedTenantId,
      setSelectedTenantId: handleSetSelectedTenantId,
      selectedTenant,
      setSelectedTenant,
      addTenant,
      setTenantDeployStatus,
      revertTenantDeployStatus,
      setTenantClientSecretExpiry
    }),
    [tenants, loading, selectedTenantId, selectedTenant]
  )

  return <TenantContext.Provider value={contextValue}>{children}</TenantContext.Provider>
}

export function useTenants() {
  return useContext(TenantContext)
}
